import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Outlet, useBlocker, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Bell, Building2, ChartColumn, ChevronsUpDown, CircleCheckBig, Crown, FolderKanban, House, Inbox, Keyboard, LayoutGrid,
  Layers, LogOut, Menu as MenuIcon, Moon, PanelLeft, Plus, Radar as RadarIcon, RefreshCw, Settings, StickyNote, Sun, UserRound, Users, X,
} from 'lucide-react';
import { openTenantStream } from '@missions/api-client';
import { useBrand, useDialogs, useToast, useUnsavedApi } from '@missions/ui';
import { api } from '../lib/client';
import { useApi, useInvalidate, usePhone } from '../lib/hooks';
import { setUnreadTitle } from '../lib/seo';
import { useSession, useT } from '../lib/session';
import { Av } from '../components/atoms';
import { Menu, type MenuItem } from '../components/Menu';
import { layerCount } from '../components/Layer';
import { NotificationsPanel } from '../features/notifications/NotificationsPanel';
import { PrefsModal } from '../features/notifications/PrefsModal';
import { CreateMissionModal } from '../features/missions/CreateMissionModal';
import { MissionPanel } from '../features/missions/MissionPanel';
import { type Chrome, type NewMissionDefaults, ShellCtx, type ShellApi } from './shell-context';

/** App shell from mockups/app.html: full-width header, grouped primary sidebar, contextual secondary sidebar, rail, mobile tab bar. */
export function AppShell() {
  const s = useSession();
  const t = useT();
  const brand = useBrand();
  const toast = useToast();
  const dialogs = useDialogs();
  const unsaved = useUnsavedApi();
  const nav = useNavigate();
  const loc = useLocation();
  const [sp, setSp] = useSearchParams();
  const phone = usePhone();
  const qc = useQueryClient();
  const invalidate = useInvalidate();

  const [chrome, setChromeState] = useState<Chrome>({ section: 'Work' });
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem('ms-nav-collapsed') === '1';
    } catch {
      return false;
    }
  });
  const [drawer, setDrawer] = useState<'p' | 's' | null>(null);
  const [focus, setFocusState] = useState(false);
  const [newMission, setNewMission] = useState<NewMissionDefaults | null>(null);
  const [npOpen, setNpOpen] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [wsOpen, setWsOpen] = useState(false);
  const [meOpen, setMeOpen] = useState(false);
  const wsRef = useRef<HTMLButtonElement>(null);
  const meRef = useRef<HTMLButtonElement>(null);
  const [wsId, setWsId] = useState<string | null>(() => {
    try {
      return localStorage.getItem('ms-ws');
    } catch {
      return null;
    }
  });

  const me = s.me!;
  const workspaces = me.workspaces;
  const ws = workspaces.find((w) => w.id === wsId) ?? workspaces[0];
  const dark = typeof document !== 'undefined' && document.documentElement.dataset.theme === 'dark';
  const canCreate = s.can('mission.create');

  // body classes: shell layout + focus mode (CSS from mockups/app.html)
  useEffect(() => {
    document.body.classList.toggle('has-staff', !!s.me?.impersonatedBy);
    return () => document.body.classList.remove('has-staff');
  }, [s.me?.impersonatedBy]);
  useEffect(() => {
    document.body.classList.add('in-shell');
    return () => document.body.classList.remove('in-shell', 'focus', 'nav-collapsed');
  }, []);
  useEffect(() => {
    document.body.classList.toggle('focus', focus);
  }, [focus]);
  useEffect(() => {
    document.body.classList.toggle('nav-collapsed', collapsed);
    try {
      localStorage.setItem('ms-nav-collapsed', collapsed ? '1' : '0');
    } catch {
      /* private mode */
    }
  }, [collapsed]);
  useEffect(() => setDrawer(null), [loc.pathname]);

  // In-app navigation with unsaved changes → designed "Keep editing / Discard / Save" dialog.
  const blocker = useBlocker(({ currentLocation, nextLocation }) => unsaved.isDirty() && currentLocation.pathname !== nextLocation.pathname);
  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    let proceeded = false;
    void unsaved
      .guard(() => {
        proceeded = true;
        blocker.proceed();
      })
      .then(() => {
        if (!proceeded) blocker.reset();
      });
  }, [blocker, unsaved]);

  const setFocus = useCallback(
    (on: boolean) => {
      setFocusState(on);
      try {
        if (on && document.fullscreenEnabled && !document.fullscreenElement) void document.documentElement.requestFullscreen().catch(() => undefined);
        else if (!on && document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
      } catch {
        /* iOS Safari: in-app focus layout only */
      }
      if (on) {
        let shown = false;
        try {
          shown = !!localStorage.getItem('ms-fs-hint');
          localStorage.setItem('ms-fs-hint', '1');
        } catch {
          /* private mode */
        }
        if (!shown) toast('Full screen on. Press Esc or F to exit.', { type: 'info' });
      }
    },
    [toast],
  );
  useEffect(() => {
    const on = () => {
      if (!document.fullscreenElement) setFocusState(false);
    };
    document.addEventListener('fullscreenchange', on);
    return () => document.removeEventListener('fullscreenchange', on);
  }, []);
  useEffect(() => {
    if (!chrome.focusable && focus) setFocusState(false);
  }, [chrome.focusable, focus]);

  const openMission = useCallback(
    (id: string) => {
      const next = new URLSearchParams(sp);
      next.set('m', id);
      setSp(next);
    },
    [sp, setSp],
  );

  const api_: ShellApi = useMemo(
    () => ({
      workspaceId: ws?.id ?? '',
      setChrome: setChromeState,
      openNewMission: (d) => setNewMission(d ?? {}),
      openMission,
      openNotifications: () => setNpOpen((o) => !o),
      openPrefs: () => {
        setNpOpen(false);
        setPrefsOpen(true);
      },
      focus,
      setFocus,
      openDrawer: setDrawer,
      closeDrawers: () => setDrawer(null),
    }),
    [openMission, focus, setFocus, ws?.id],
  );

  /* ---------- notifications: counts + live stream ---------- */
  const counts = useApi('notifications.counts', undefined, { staleTime: 15_000 });
  const unseen = counts.data?.unseen ?? 0;
  useEffect(() => setUnreadTitle(unseen), [unseen]);
  const [shake, setShake] = useState(0);
  useEffect(() => {
    const h = openTenantStream(
      api,
      {
        'notification.created': (n) => {
          void invalidate('notifications');
          setShake((x) => x + 1);
          if (!npOpen && loc.pathname !== '/inbox') toast(n.title, { type: 'info', action: n.href ? { label: 'View', onClick: () => nav(n.href!) } : undefined });
        },
        'notification.counts': (c) => {
          qc.setQueryData<typeof c>(['missions', 'notifications.counts', null, null], c);
        },
        'mission.updated': () => void invalidate('missions', 'myMissions', 'missionControl'),
        'mission.deleted': () => void invalidate('missions', 'myMissions', 'missionControl'),
        'timeline.appended': () => void invalidate('timeline'),
        'tenant.state_changed': () => void s.refetchMe(),
        'session.revoked': () => void s.signOut(),
      },
      { onDisconnect: () => void invalidate('notifications.counts') },
    );
    const poll = setInterval(() => void invalidate('notifications.counts'), 30_000);
    return () => {
      h.close();
      clearInterval(poll);
    };
    // stream lifetime = shell lifetime
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------- keyboard shortcuts ---------- */
  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName?.toLowerCase();
      if (['input', 'textarea', 'select'].includes(tag) || (e.target as HTMLElement).isContentEditable || e.metaKey || e.ctrlKey || e.altKey) return;
      if (document.querySelector('.ui-dialog-wrap, .ui-pop, .ui-sheet, .ui-version-block')) return;
      if (e.key === 'Escape' && focus && layerCount() === 0) {
        setFocus(false);
        return;
      }
      if (e.key === 'c' && canCreate && layerCount() === 0) {
        e.preventDefault();
        setNewMission({});
      }
      if (e.key === 'n') {
        e.preventDefault();
        setNpOpen((o) => !o);
      }
      if ((e.key === 'f' || e.key === 'F') && chrome.focusable && layerCount() === 0) {
        e.preventDefault();
        setFocus(!focus);
      }
      if (e.key === '?') {
        void dialogs.alert({
          title: 'Keyboard shortcuts',
          message: (
            <span>
              <b>C</b>: {t.createItem.toLowerCase()} · <b>N</b>: notifications · <b>F</b>: full screen · <b>/</b>: search · <b>J / K</b>: next / previous {t.item} · <b>Esc</b>: close
            </span>
          ),
        });
      }
    };
    document.addEventListener('keydown', on);
    return () => document.removeEventListener('keydown', on);
  }, [focus, setFocus, canCreate, chrome.focusable, dialogs, t]);

  const homeIcon = brand.missionLanguage ? <RadarIcon /> : <House />;
  const navGroups: { label: string; items: { to: string; label: string; icon: ReactNode; end?: boolean; count?: number; muted?: boolean }[] }[] = [
    {
      label: 'Work',
      items: [
        { to: '/', label: t.home, icon: homeIcon, end: true },
        { to: '/my', label: t.myItems, icon: <CircleCheckBig /> },
        { to: '/inbox', label: 'Inbox', icon: <Inbox />, count: unseen },
      ],
    },
    {
      label: 'Projects',
      items: [
        { to: '/projects', label: 'Projects', icon: <FolderKanban /> },
        { to: '/clients', label: 'Clients', icon: <Building2 /> },
        { to: '/boards', label: 'Sticky Boards', icon: <StickyNote /> },
      ],
    },
    { label: 'Insights', items: [{ to: '/reports', label: 'Reports', icon: <ChartColumn /> }] },
    {
      label: 'Manage',
      items: [
        { to: '/settings/members', label: 'Team', icon: <Users /> },
        { to: '/settings', label: 'Settings', icon: <Settings /> },
      ],
    },
  ];
  const projectsActive = loc.pathname.startsWith('/p/') || loc.pathname === '/projects';

  const meItems: MenuItem[] = [
    { header: me.user.email },
    { label: 'My account', icon: <UserRound />, onClick: () => nav('/settings/account') },
    { label: 'Notification preferences', icon: <Bell />, onClick: () => setPrefsOpen(true) },
    { label: 'Keyboard shortcuts', icon: <Keyboard />, hint: '?', onClick: () => document.dispatchEvent(new KeyboardEvent('keydown', { key: '?' })) },
    { label: 'Check for updates', icon: <RefreshCw />, onClick: () => toast('You’re on the latest version', { type: 'info' }) },
    '-',
    {
      label: 'Sign out',
      icon: <LogOut />,
      onClick: () =>
        void unsaved.guard(() => {
          void s.signOut();
        }),
    },
  ];
  const wsItems: MenuItem[] = [
    { header: 'Workspaces' },
    ...workspaces.map((w) => ({
      label: w.name,
      icon: <Layers />,
      selected: w.id === ws?.id,
      onClick: () => {
        setWsId(w.id);
        try {
          localStorage.setItem('ms-ws', w.id);
        } catch {
          /* private mode */
        }
        toast(`Switched to ${w.name}`, { type: 'info' });
      },
    })),
    '-',
    {
      label: 'Create workspace',
      icon: <Plus />,
      disabled: !s.can('workspace.create'),
      lock: 'Admin',
      onClick: () => nav('/settings/workspaces'),
    },
  ];

  const pside = (
    <aside className={`pside${drawer === 'p' ? ' open' : ''}`} id="pside" aria-label="Main navigation">
      <div className="drawer-head">
        <img className="brand-light" src={s.tenant.logoUrl ?? '/brand/missions-logo.png'} alt={s.tenant.appTitle} />
        <img className="brand-dark" src={s.tenant.logoDarkUrl ?? s.tenant.logoUrl ?? '/brand/missions-logo-dark.png'} alt={s.tenant.appTitle} />
        <span className="sp" />
        <button className="ib" onClick={() => setDrawer(null)} aria-label="Close menu"><X /></button>
      </div>
      <div className="p-scroll">
        {navGroups.map((g) => (
          <div key={g.label} style={{ display: 'contents' }}>
            <div className="glabel">{g.label}</div>
            {g.items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end ?? it.to === '/settings'}
                className={({ isActive }) => `pi${isActive || (it.to === '/projects' && projectsActive) ? ' on' : ''}`}
                data-tip={it.label}
              >
                {it.icon}
                <span className="lbl">{it.label}</span>
                {it.count ? <span className="cnt">{it.count > 9 ? '9+' : it.count}</span> : null}
              </NavLink>
            ))}
          </div>
        ))}
      </div>
      {s.tenant.showPoweredBy && (
        <div className="p-foot">
          <a className="pwr-dnw" href="https://devninjaweb.com" data-tooltip="Powered by DevNinjaWeb" aria-label="Powered by DevNinjaWeb" target="_blank" rel="noopener">
            Powered by <img src="/brand/dnw-logo.png" alt="DevNinjaWeb" />
          </a>
        </div>
      )}
    </aside>
  );

  return (
    <ShellCtx.Provider value={api_}>
      <div className={`shell${chrome.rail ? '' : ' no-rail'}`} id="shell">
        <header className="hdr" role="banner">
          <button className="ib mob" onClick={() => setDrawer('p')} aria-label="Open menu"><MenuIcon /></button>
          <button className="ib" id="collapseBtn" onClick={() => setCollapsed((c) => !c)} aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} data-tooltip={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
            <PanelLeft />
          </button>
          <NavLink to="/" aria-label={`${s.tenant.appTitle} home`} style={{ display: 'contents' }}>
            <img className="logo brand-light" src={s.tenant.logoUrl ?? '/brand/missions-logo.png'} alt={s.tenant.appTitle} />
            <img className="logo brand-dark" src={s.tenant.logoDarkUrl ?? s.tenant.logoUrl ?? '/brand/missions-logo-dark.png'} alt={s.tenant.appTitle} />
          </NavLink>
          <span className="slash">/</span>
          <button className="wsbtn" ref={wsRef} onClick={() => setWsOpen((o) => !o)} aria-haspopup="menu" aria-expanded={wsOpen}>
            <span className="wsi"><Layers /></span>
            <span>{ws?.name ?? 'Workspace'}</span>
            <span className="crown" data-tooltip={`${me.subscription.planName} plan`}><Crown /></span>
            <ChevronsUpDown className="ud" />
          </button>
          <Menu anchorRef={wsRef} open={wsOpen} onClose={() => setWsOpen(false)} title="Switch workspace" items={wsItems} />
          <span className="slash">/</span>
          <span className="crumb-sec">{chrome.section}</span>
          {chrome.current && <span className="slash hide-t">/</span>}
          {chrome.current && <span className="crumb-cur">{chrome.current}</span>}
          <span className="sp" />
          {chrome.projectActions}
          {canCreate && !phone && (
            <button className="btn btn-p hide-m" onClick={() => setNewMission({})} aria-label={t.newItem} data-tooltip={t.newItem} data-shortcut="C">
              <Plus />
              <span className="bl">{t.newItem}</span>
            </button>
          )}
          <span className="vdiv hide-m" />
          <button
            key={shake}
            className={`ib bell${shake ? ' shake' : ''}`}
            onClick={() => setNpOpen((o) => !o)}
            aria-label={`Notifications, ${unseen} new, ${counts.data?.unread ?? 0} unread`}
            aria-haspopup="dialog"
            data-tooltip="Notifications"
            data-shortcut="N"
          >
            <Bell />
            {unseen > 0 && <span className="bell-badge">{unseen > 9 ? '9+' : unseen}</span>}
          </button>
          <button className="ib theme-btn" onClick={() => brand.setTheme(dark ? 'light' : 'dark')} aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'} data-tooltip={dark ? 'Switch to light mode' : 'Switch to dark mode'}>
            <Sun className="sun" />
            <Moon className="moon" />
          </button>
          <button className="me" ref={meRef} onClick={() => setMeOpen((o) => !o)} aria-label="Account menu" aria-haspopup="menu" data-tooltip={me.user.name}>
            <Av u={me.user} tip={false} />
            <span className="online" />
          </button>
          <Menu anchorRef={meRef} open={meOpen} onClose={() => setMeOpen(false)} title={me.user.name} items={meItems} alignRight />
        </header>

        {me.impersonatedBy && (
          <div className="staff-strip" role="status" aria-live="polite">
            <span className="ss-txt">
              <b>You’re inside {me.tenant.name} as DevNinjaWeb staff</b>
              <span className="hide-m"> · {me.impersonatedBy.name} · every change is logged</span>
            </span>
            <button
              type="button"
              className="ss-exit"
              onClick={async () => {
                await s.signOut();
                try {
                  window.close();
                } catch {
                  /* not opened by script */
                }
              }}
            >
              <LogOut />Exit
            </button>
          </div>
        )}

        {pside}

        <aside className={`sside${drawer === 's' ? ' open' : ''}`} id="sside" aria-label="Section navigation">
          <div className="drawer-head">
            <button className="ib" onClick={() => setDrawer('p')} aria-label="Back to menu"><ArrowLeft /></button>
            <b>{chrome.secondaryTitle ?? chrome.section}</b>
            <span className="sp" />
            <button className="ib" onClick={() => setDrawer(null)} aria-label="Close"><X /></button>
          </div>
          <div className="s-scroll">{chrome.secondary}</div>
        </aside>

        <main className="main" id="main">
          <Outlet />
        </main>

        <aside className="rail" id="rail" aria-label="Page details">{chrome.rail}</aside>
      </div>

      <div className={`backdrop${drawer ? ' show' : ''}`} style={{ zIndex: 99 }} onClick={() => setDrawer(null)} aria-hidden="true" />

      <nav className="tabbar" aria-label="Primary">
        <NavLink to="/" end className={({ isActive }) => (isActive ? 'on' : '')}>{homeIcon}<span>{brand.missionLanguage ? 'Control' : 'Home'}</span></NavLink>
        <NavLink to="/my" className={({ isActive }) => (isActive ? 'on' : '')}><CircleCheckBig />{t.myItems}</NavLink>
        <NavLink to="/inbox" className={({ isActive }) => (isActive ? 'on' : '')}>
          <Inbox />Inbox{unseen > 0 && <span className="tb-badge">{unseen > 9 ? '9+' : unseen}</span>}
        </NavLink>
        <NavLink to="/projects" className={() => (projectsActive ? 'on' : '')}><FolderKanban />Projects</NavLink>
        <button type="button" onClick={() => setDrawer('p')}><LayoutGrid />More</button>
      </nav>
      {canCreate && phone && !chrome.hideFab && !focus && (
        <button className="fab" onClick={() => setNewMission({})} aria-label={t.newItem}><Plus /></button>
      )}

      <NotificationsPanel open={npOpen} onClose={() => setNpOpen(false)} />
      <PrefsModal open={prefsOpen} onClose={() => setPrefsOpen(false)} />
      <CreateMissionModal defaults={newMission} onClose={() => setNewMission(null)} />
      <MissionPanel />
    </ShellCtx.Provider>
  );
}
